import React, { useEffect, useState } from 'react';
import Navbar from "./Navbar";
import Footer from "./Footer.jsx";
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';

// API Base URL
const API_BASE_URL = 'https://royaleheritagefiesta.com/royaleheritagefiesta/api';

const RoyalHeritageFestia = () => {
  const navigate = useNavigate();
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [showFloatingButtons, setShowFloatingButtons] = useState(false);
  const [upcomingHeritageEvents, setUpcomingHeritageEvents] = useState([]);
  const [featuredHeritage, setFeaturedHeritage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  
  // Form submission states
  const [formLoading, setFormLoading] = useState(false);
  const [formSuccess, setFormSuccess] = useState('');
  const [formError, setFormError] = useState('');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    eventType: 'Royal Heritage Festia',
    guests: '',
    preferredDate: '',
    specialRequests: ''
  });

  // Featured event countdown state
  const [featuredEventTimeLeft, setFeaturedEventTimeLeft] = useState({ days: '00', hours: '00', minutes: '00', seconds: '00' });

  // ========== WEBPAGE CONTENT STATE ==========
  const [webpageContent, setWebpageContent] = useState({
    home: {
      booking: {
        whatsapp_number: "918688365247",
        button_text: "Send via WhatsApp"
      }
    },
    images: {}
  });

  // Fetch webpage content - COMMENTED OUT (using static)
  useEffect(() => {
    console.log("Using static webpage content");
  }, []);

  // Function to get image URL from backend
  const getImageUrl = (imageKey) => {
    if (!webpageContent.images) return null;
    const imgPath = webpageContent.images[imageKey];
    if (!imgPath) return null;
    if (imgPath.startsWith('http')) return imgPath;
    return `http://localhost:8007${imgPath}`;
  };

  // Function to get event image based on index
  const getEventImageByIndex = (index) => {
    const imageKeys = {
      0: 'upcoming_event_1',
      1: 'upcoming_event_2',
      2: 'upcoming_event_3',
      3: 'upcoming_event_4'
    };
    const uploadedImage = getImageUrl(imageKeys[index]);
    if (uploadedImage) return uploadedImage;
    return null;
  };

  // Dynamic image mapping for Royal Heritage Festia
  const getHeritageImage = (eventName, index) => {
    const heritageImages = [
      'https://images.unsplash.com/photo-1536392706976-e486e2ba97af?q=80&w=800&auto=format',
      'https://images.unsplash.com/photo-1711869206287-33a83984d6ee?w=800&auto=format',
      'https://images.unsplash.com/photo-1711867623677-d34515fe679b?w=800&auto=format',
      'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=800&auto=format'
    ];
    return heritageImages[index % heritageImages.length];
  };

  // Fetch events from PHP API - UPDATED with correct eventType filter
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        setApiError(null);
        
        // Fetch from PHP API
        const response = await axios.get(`${API_BASE_URL}/events.php?all`);
        console.log("All events from PHP API:", response.data);
        
        if (response.data && Array.isArray(response.data)) {
          const today = new Date();
          
          // Filter ONLY Royal Heritage Festia events (exact match)
          const heritageEvents = response.data
            .filter(event => {
              // Exact match for 'Royal Heritage Festia' (case insensitive)
              const eventType = event.eventType?.trim() || '';
              return eventType === 'Royal Heritage Festia';
            })
            .filter(event => new Date(event.eventDate) > today)
            .sort((a, b) => new Date(a.eventDate) - new Date(b.eventDate));
          
          console.log("Filtered Royal Heritage Festia events:", heritageEvents);
          
          // Add images to events
          const heritageEventsWithImages = heritageEvents.map((event, idx) => {
            const uploadedImage = getEventImageByIndex(idx);
            return {
              ...event,
              img: uploadedImage || getHeritageImage(event.eventName, idx)
            };
          });
          
          setUpcomingHeritageEvents(heritageEventsWithImages);
          
          if (heritageEventsWithImages.length > 0) {
            setFeaturedHeritage(heritageEventsWithImages[0]);
          }
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching events:", error);
        setApiError("Could not load events from server.");
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  // Countdown timer for featured heritage event
  useEffect(() => {
    if (!featuredHeritage) return;

    const timer = setInterval(() => {
      const now = new Date().getTime();
      const eventDateTime = new Date(featuredHeritage.eventDate).getTime();
      const distance = eventDateTime - now;

      if (distance < 0) {
        setFeaturedEventTimeLeft({ days: '00', hours: '00', minutes: '00', seconds: '00' });
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      setFeaturedEventTimeLeft({
        days: days.toString().padStart(2, '0'),
        hours: hours.toString().padStart(2, '0'),
        minutes: minutes.toString().padStart(2, '0'),
        seconds: seconds.toString().padStart(2, '0'),
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [featuredHeritage]);

  // Scroll handlers
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
      setShowFloatingButtons(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleBackToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleWhatsAppClick = () => {
    window.open(`https://wa.me/${webpageContent.home.booking.whatsapp_number || '918688365247'}?text=Hi%20Royal%20Heritage%20Festia%20team,%20I%27m%20interested%20in%20Royal%20Heritage%20Festia.`, '_blank');
  };

  const scrollToSection = (sectionId) => {
    if (sectionId === 'home') {
      navigate('/');
    } else {
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "TBA";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return dateString;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const generateWhatsAppMessage = (data) => {
    const message = `*ROYAL HERITAGE FESTIA - Royal Heritage Experience Booking*%0A%0A` +
      `*Contact Information:*%0A` +
      `• Name: ${data.fullName}%0A` +
      `• Email: ${data.email}%0A` +
      `• Phone: ${data.phone}%0A%0A` +
      `*Event Details:*%0A` +
      `• Event Type: ${data.eventType}%0A` +
      `• Number of Guests: ${data.guests}%0A` +
      `• Preferred Date: ${data.preferredDate}%0A%0A` +
      `*Special Requests:*%0A${data.specialRequests || 'None specified'}%0A%0A` +
      `---%0A*This enquiry was submitted from Royal Heritage Festia - Royal Heritage Festia page.*`;
    
    return encodeURIComponent(message);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormSuccess('');
    setFormError('');

    if (!formData.fullName || !formData.email || !formData.phone || !formData.guests || !formData.preferredDate) {
      setFormError('Please fill in all required fields marked with *');
      setFormLoading(false);
      setTimeout(() => setFormError(''), 3000);
      return;
    }

    if (formData.phone.length !== 10) {
      setFormError('Please enter a valid 10-digit phone number');
      setFormLoading(false);
      setTimeout(() => setFormError(''), 3000);
      return;
    }

    try {
      alert("✓ Your Royal Heritage experience request has been prepared!\n\nYou will now be redirected to WhatsApp to send your enquiry.");
      
      const message = generateWhatsAppMessage(formData);
      const whatsappUrl = `https://wa.me/${webpageContent.home.booking.whatsapp_number || '918688365247'}?text=${message}`;
      
      window.open(whatsappUrl, '_blank');
      
      setFormSuccess('Thank you! Your enquiry has been sent via WhatsApp. We will get back to you shortly.');
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        eventType: 'Royal Heritage Festia',
        guests: '',
        preferredDate: '',
        specialRequests: ''
      });
      
    } catch (error) {
      console.error('Form submission error:', error);
      setFormError('Something went wrong. Please try again or contact us directly.');
    } finally {
      setFormLoading(false);
      setTimeout(() => {
        setFormSuccess('');
        setFormError('');
      }, 5000);
    }
  };

  const navigateToEventDetails = (event) => {
    navigate('/eventCard', { 
      state: { 
        eventId: event.eventId,
        eventData: event 
      } 
    });
  };

  const navigateToHeritageBooking = (event, e) => {
    e.stopPropagation();
    navigate('/BookingTicket', { 
      state: { 
        eventId: event.eventId,
        eventData: event 
      } 
    });
  };

  return (
    <>
      <Navbar />

      <style>{`
        /* ===== ROYAL HERITAGE THEME STYLING ===== */
        body { 
          background: #111; 
          font-family: 'Montserrat', sans-serif; 
          margin: 0; 
          color: #333; 
        }

        /* MAIN CONTAINER */
        .main-container {
          background: #f5f5f5;
          margin: 0 auto;
          width: 98%;
          max-width: 1400px;
          border-left: 17px solid #000;
          border-right: 17px solid #000;
          border-radius: 0;
          padding: 1rem;
          min-height: calc(100vh - 200px);
        }

        /* Hero Section */
        .hero-section {
          position: relative;
          padding: 4rem 2rem;
          background: linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.7)), url('https://images.unsplash.com/photo-1536392706976-e486e2ba97af?q=80&w=1600&auto=format');
          background-size: cover;
          background-position: center;
          border-radius: 16px;
          margin-bottom: 30px;
          text-align: center;
        }

        .hero-section h1 {
          font-family: 'Cinzel', serif;
          color: white;
          font-size: 3rem;
          font-weight: 700;
          margin-bottom: 1rem;
          text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
        }

        .hero-section p {
          color: rgba(255,255,255,0.9) !important;
          font-size: 1.1rem;
          max-width: 700px;
          margin: 0 auto;
        }

        .hero-badge {
          display: inline-block;
          background: rgba(184,134,11,0.2);
          backdrop-filter: blur(5px);
          color: white;
          padding: 0.5rem 1.5rem;
          border-radius: 40px;
          font-weight: 500;
          margin-bottom: 2rem;
          border: 1px solid rgba(255,255,255,0.3);
          font-size: 12px;
          letter-spacing: 2px;
        }

        /* SECTION TITLE */
        .section-title {
          text-align: center;
          margin: 40px 0 30px;
        }
        .section-title h2 {
          font-family: 'Cinzel', serif;
          font-size: 32px;
          font-weight: 700;
          color: #000;
          position: relative;
          display: inline-block;
          padding-bottom: 12px;
        }
        .section-title h2:after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 60px;
          height: 2px;
          background: #b8860b;
        }
        .section-title p {
          color: #666;
          font-size: 14px;
          margin-top: 12px;
        }
        .section-header-with-link {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
          flex-wrap: wrap;
          gap: 15px;
        }
        .section-tag {
          color: #b8860b;
          font-size: 12px;
          letter-spacing: 2px;
          text-transform: uppercase;
          margin-bottom: 10px;
          display: inline-block;
        }

        /* BUTTONS */
        .btn-custom {
          border: 1px solid #b8860b;
          background: transparent;
          color: #b8860b;
          padding: 10px 25px;
          border-radius: 30px;
          font-size: 13px;
          font-weight: 500;
          transition: 0.3s;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .btn-custom:hover {
          background: #b8860b;
          color: #fff;
          border-color: #b8860b;
        }

        /* FEATURED HERITAGE EVENT */
        .featured-section {
          background: #f5f5f5;
          padding: 40px 0;
          margin: 20px 0;
        }
        .featured-card {
          background: #fff;
          border-radius: 15px;
          overflow: hidden;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0;
          box-shadow: 0 5px 20px rgba(0,0,0,0.05);
        }
        .featured-image {
          height: 450px;
          overflow: hidden;
        }
        .featured-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .featured-content {
          padding: 40px;
        }
        .featured-badge {
          background: #b8860b;
          color: #fff;
          padding: 5px 15px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 600;
          display: inline-block;
          margin-bottom: 20px;
        }
        .featured-content h3 {
          font-family: 'Cinzel', serif;
          font-size: 28px;
          font-weight: 700;
          margin-bottom: 20px;
          color: #000;
        }
        .featured-content p {
          color: #666;
          margin-bottom: 20px;
          line-height: 1.6;
        }
        .featured-details {
          display: flex;
          gap: 20px;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }
        .featured-details .detail {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: #666;
        }
        .featured-timer {
          display: flex;
          align-items: center;
          gap: 15px;
          background: #f5f5f5;
          padding: 12px 20px;
          border-radius: 40px;
          margin: 20px 0;
          width: fit-content;
          color: #333 !important;
        }
        .featured-actions {
          display: flex;
          gap: 15px;
          flex-wrap: wrap;
        }
        .btn-book {
          padding: 10px 25px;
          border-radius: 30px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: 0.3s;
          border: none;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #b8860b;
          color: #fff;
          border: 1px solid #b8860b;
        }
        .btn-book:hover {
          background: transparent;
          color: #b8860b;
        }

        /* EVENTS GRID */
        .events-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 25px;
          margin-top: 20px;
        }
        
        @media (max-width: 992px) {
          .events-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        
        @media (max-width: 768px) {
          .events-grid {
            grid-template-columns: 1fr;
          }
        }
        
        .event-card {
          border-radius: 15px;
          overflow: hidden;
          cursor: pointer;
          transition: 0.3s;
          background: #fff;
          height: 100%;
          display: flex;
          flex-direction: column;
          box-shadow: 0 3px 10px rgba(0,0,0,0.05);
        }
        .event-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 25px rgba(184,134,11,0.1);
        }
        .event-card-image {
          height: 200px;
          overflow: hidden;
        }
        .event-card-image img {
          height: 100%;
          width: 100%;
          object-fit: cover;
          transition: 0.5s;
        }
        .event-card:hover .event-card-image img {
          transform: scale(1.05);
        }
        .event-card-content {
          padding: 20px;
          flex-grow: 1;
        }
        .event-tag {
          color: #b8860b;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
          display: inline-block;
          margin-bottom: 10px;
        }
        .event-card-content h4 {
          font-family: 'Cinzel', serif;
          font-size: 18px;
          font-weight: 700;
          margin: 10px 0;
          color: #000;
        }
        .event-meta {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin: 15px 0;
        }
        .event-meta span {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #666;
          font-size: 12px;
        }
        .event-timer {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #f5f5f5;
          padding: 8px 12px;
          border-radius: 8px;
          margin: 15px 0;
          font-size: 12px;
        }
        .event-actions {
          display: flex;
          gap: 10px;
          margin-top: auto;
        }
        .btn-book-small {
          flex: 1;
          padding: 8px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 500;
          cursor: pointer;
          transition: 0.3s;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 5px;
          background: #b8860b;
          color: #fff;
          border: 1px solid #b8860b;
        }
        .btn-book-small:hover {
          background: transparent;
          color: #b8860b;
        }

        /* BOOKING FORM SECTION */
        .booking-section {
          background: #fff;
          padding: 50px 0;
          margin: 20px 0;
          border-radius: 15px;
          box-shadow: 0 5px 20px rgba(0,0,0,0.05);
        }
        .booking-card {
          max-width: 900px;
          margin: 0 auto;
          padding: 30px;
        }
        .booking-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }
        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .form-group.full-width {
          grid-column: span 2;
        }
        .form-label {
          font-weight: 500;
          color: #333;
          font-size: 13px;
        }
        .form-control, .form-select {
          padding: 12px 15px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-family: 'Montserrat', sans-serif;
          font-size: 13px;
          transition: 0.3s;
        }
        .form-control:focus, .form-select:focus {
          border-color: #b8860b;
          outline: none;
          box-shadow: 0 0 0 2px rgba(184,134,11,0.1);
        }
        .alert-success {
          background: #d4edda;
          color: #155724;
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 20px;
          font-size: 13px;
        }
        .alert-danger {
          background: #f8d7da;
          color: #721c24;
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 20px;
          font-size: 13px;
        }
        .btn-submit {
          background: #b8860b;
          color: #fff;
          border: none;
          padding: 14px 30px;
          border-radius: 40px;
          font-weight: 600;
          font-size: 14px;
          cursor: pointer;
          transition: 0.3s;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          margin-top: 10px;
          width: 100%;
          justify-content: center;
        }
        .btn-submit:hover {
          background: #9b6e09;
          transform: translateY(-2px);
        }

        /* Info Cards - Royal Heritage Festia Specific */
        .info-cards {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          margin: 30px 0;
        }
        
        @media (max-width: 992px) {
          .info-cards {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        
        @media (max-width: 768px) {
          .info-cards {
            grid-template-columns: 1fr;
          }
        }
        
        .info-card {
          background: #fff;
          padding: 25px 20px;
          text-align: center;
          border-radius: 12px;
          border: 1px solid #eee;
          transition: 0.3s;
        }
        .info-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 25px rgba(184,134,11,0.1);
          border-color: #b8860b;
        }
        .info-icon {
          font-size: 2rem;
          color: #b8860b;
          margin-bottom: 15px;
        }
        .info-card h4 {
          font-family: 'Cinzel', serif;
          font-size: 1rem;
          font-weight: 700;
          margin-bottom: 8px;
          color: #000;
        }
        .info-card p {
          color: #666;
          font-size: 0.85rem;
          margin: 0;
        }

        /* FLOATING BUTTONS */
        .floating-buttons {
          position: fixed;
          bottom: 20px;
          right: 20px;
          display: flex;
          flex-direction: column;
          gap: 10px;
          z-index: 1000;
          opacity: 0;
          visibility: hidden;
          transform: translateY(20px);
          transition: 0.3s;
        }
        .floating-buttons.show {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }
        .floating-btn {
          width: 45px;
          height: 45px;
          border-radius: 50%;
          border: none;
          cursor: pointer;
          transition: 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          color: #fff;
          box-shadow: 0 2px 10px rgba(0,0,0,0.2);
        }
        .floating-btn.whatsapp {
          background: #25d366;
        }
        .floating-btn.top {
          background: #b8860b;
        }
        .floating-btn:hover {
          transform: scale(1.1);
        }
        .back-to-top {
          position: fixed;
          bottom: 20px;
          right: 20px;
          width: 45px;
          height: 45px;
          background: #b8860b;
          color: #fff;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          opacity: 0;
          visibility: hidden;
          transition: 0.2s;
          z-index: 99;
        }
        .back-to-top.show {
          opacity: 1;
          visibility: visible;
        }

        /* RESPONSIVE */
        @media (max-width: 992px) {
          .main-container {
            width: 96%;
            padding: 0.8rem;
          }
          .featured-card {
            grid-template-columns: 1fr;
          }
          .hero-section h1 {
            font-size: 2.5rem;
          }
          .hero-section {
            padding: 3rem 1rem;
          }
        }
        
        @media (max-width: 768px) {
          .main-container {
            width: 100%;
            padding: 0.5rem;
            border-left: 14px solid #000 !important;
            border-right: 14px solid #000 !important;
          }
          .hero-section h1 {
            font-size: 2rem;
          }
          .hero-section p {
            font-size: 0.9rem;
          }
          .section-title h2 {
            font-size: 24px;
          }
          .featured-content {
            padding: 25px;
          }
          .booking-card {
            padding: 20px;
          }
          .form-row {
            grid-template-columns: 1fr;
          }
          .form-group.full-width {
            grid-column: span 1;
          }
        }
        
        @media (max-width: 576px) {
          .main-container {
            border-left: 17.5px solid #000 !important;
            border-right: 17.5px solid #000 !important;
          }
          .hero-section h1 {
            font-size: 1.5rem;
          }
          .hero-badge {
            font-size: 10px;
          }
          .featured-actions {
            flex-direction: column;
          }
          .btn-book {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>

      {/* MAIN CONTAINER */}
      <div className="main-container">

        {/* 1. UPCOMING ROYAL HERITAGE EVENTS SECTION (FIRST) */}
        <div className="section-header-with-link">
          <div>
            <span className="section-tag">✦ UPCOMING ROYAL HERITAGE EVENTS</span>
            <h2 className="section-title" style={{ margin: 0, color: "#000" }}>Featured Heritage Events</h2>
          </div>
          <Link to="/eventcalander_2" className="btn-custom">View all <i className="bi bi-arrow-right"></i></Link>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-secondary">Loading royal heritage events...</p>
          </div>
        ) : upcomingHeritageEvents.length > 0 ? (
          <div className="events-grid">
            {upcomingHeritageEvents.map((event, idx) => {
              const eventDateTime = new Date(event.eventDate).getTime();
              const now = new Date().getTime();
              const distance = eventDateTime - now;
              const days = Math.floor(distance / (1000 * 60 * 60 * 24));
              const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
              
              return (
                <div className="event-card" key={event.eventId || idx} onClick={() => navigateToEventDetails(event)}>
                  <div className="event-card-image">
                    <img src={event.img} alt={event.eventName} />
                  </div>
                  <div className="event-card-content">
                    <span className="event-tag">ROYAL HERITAGE</span>
                    <h4>{event.eventName}</h4>
                    <div className="event-meta">
                      <span><i className="bi bi-calendar3"></i> {formatDate(event.eventDate)}</span>
                      <span><i className="bi bi-geo-alt"></i> {event.place || "TBA"}</span>
                    </div>
                    <div className="event-timer">
                      <i className="bi bi-clock-history"></i>
                      <span>Starts in: {days}d {hours}h</span>
                    </div>
                    <div className="event-actions" onClick={(e) => e.stopPropagation()}>
                      <button className="btn-book-small" onClick={(e) => navigateToHeritageBooking(event, e)}>
                        <i className="bi bi-ticket"></i> Book Now
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-5">
            <p className="text-secondary">No upcoming royal heritage events at the moment. Check back soon!</p>
          </div>
        )}

        {/* 2. HERO SECTION - Royal Heritage Festia */}
        <section className="hero-section">
          <div className="container text-center">
            <span className="hero-badge">✦ WHERE LEGACY MEETS CELEBRATION</span>
            <h1>Royal Heritage Fiesta</h1>
            <p>Experience timeless elegance and grandeur like never before. Where tradition meets celebration in perfect harmony.</p>
          </div>
        </section>

        {/* 3. FEATURED HERITAGE EVENT SECTION */}
        {featuredHeritage && (
          <div className="featured-section">
            <div className="featured-card">
              <div className="featured-image">
                <img src={featuredHeritage.img} alt={featuredHeritage.eventName} />
              </div>
              <div className="featured-content">
                <span className="featured-badge" style={{ background: '#b8860b', color: '#fff' }}>FEATURED HERITAGE EVENT</span>
                <h3>{featuredHeritage.eventName}</h3>
                <p>{featuredHeritage.highlights || "Immerse yourself in the grandeur of royal traditions, cultural performances, and timeless elegance."}</p>
                <div className="featured-details">
                  <span className="detail"><i className="bi bi-calendar-event"></i> {formatDate(featuredHeritage.eventDate)}</span>
                  <span className="detail"><i className="bi bi-geo-alt"></i> {featuredHeritage.place || "TBA"}</span>
                </div>
                <div className="featured-timer">
                  <i className="bi bi-clock"></i>
                  <span>Event starts in: {featuredEventTimeLeft.days}d {featuredEventTimeLeft.hours}h {featuredEventTimeLeft.minutes}m {featuredEventTimeLeft.seconds}s</span>
                </div>
                <div className="featured-actions">
                  <button className="btn-book" onClick={(e) => navigateToHeritageBooking(featuredHeritage, e)}>
                    <i className="bi bi-ticket"></i> Reserve Your Experience
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 4. INFO CARDS - Royal Heritage Festia Features */}
        <div className="info-cards">
          <div className="info-card">
            <div className="info-icon">👑</div>
            <h4>Royal Processions</h4>
            <p>Grand ceremonial experiences fit for royalty</p>
          </div>
          <div className="info-card">
            <div className="info-icon">🏰</div>
            <h4>Heritage Walks</h4>
            <p>Explore the rich legacy of royal traditions</p>
          </div>
          <div className="info-card">
            <div className="info-icon">💃</div>
            <h4>Cultural Performances</h4>
            <p>Traditional arts, music and dance</p>
          </div>
          <div className="info-card">
            <div className="info-icon">🍛</div>
            <h4>Royal Cuisine</h4>
            <p>Authentic royal delicacies and feasts</p>
          </div>
        </div>

        {/* 5. BOOKING FORM SECTION */}
        <div className="booking-section" id="booking">
          <div className="booking-card">
            <div className="section-title" style={{ marginTop: 0 }}>
              <h2>Reserve Your Royal Heritage Experience</h2>
              <p>Share your details and we'll get back to you via WhatsApp</p>
            </div>
            
            {formSuccess && <div className="alert-success">{formSuccess}</div>}
            {formError && <div className="alert-danger">{formError}</div>}
            
            <form className="booking-form" onSubmit={handleFormSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Full Name *</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    placeholder="Enter your full name" 
                    required 
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email Address *</label>
                  <input 
                    type="email" 
                    className="form-control" 
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="you@example.com" 
                    required 
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Phone Number *</label>
                  <input 
                    type="tel" 
                    className="form-control" 
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="10-digit mobile number" 
                    required 
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Number of Guests *</label>
                  <input 
                    type="number" 
                    className="form-control" 
                    name="guests"
                    value={formData.guests}
                    onChange={handleInputChange}
                    placeholder="Number of guests" 
                    min="1"
                    required 
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Preferred Date *</label>
                  <input 
                    type="date" 
                    className="form-control" 
                    name="preferredDate"
                    value={formData.preferredDate}
                    onChange={handleInputChange}
                    min={new Date().toISOString().split('T')[0]} 
                    required 
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Event Type</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    name="eventType"
                    value={formData.eventType}
                    readOnly
                  />
                </div>
              </div>
              
              <div className="form-group full-width">
                <label className="form-label">Special Requests / Requirements</label>
                <textarea 
                  className="form-control" 
                  name="specialRequests"
                  value={formData.specialRequests}
                  onChange={handleInputChange}
                  rows="3" 
                  placeholder="Please let us know about any special requirements, accessibility needs, or preferences for your royal heritage experience..."
                />
              </div>
              
              <div className="form-submit">
                <button type="submit" className="btn-submit" disabled={formLoading}>
                  <i className="bi bi-whatsapp"></i> {formLoading ? 'Sending...' : 'Send via WhatsApp'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <Footer />

      {/* Floating Buttons */}
      <div className={`floating-buttons ${showFloatingButtons ? 'show' : ''}`}>
        <button className="floating-btn" onClick={() => scrollToSection('home')}><i className="bi bi-house-door"></i></button>
        <button className="floating-btn whatsapp" onClick={handleWhatsAppClick}><i className="bi bi-whatsapp"></i></button>
        <button className="floating-btn top" onClick={handleBackToTop}><i className="bi bi-arrow-up"></i></button>
      </div>
      <div className={`back-to-top ${showBackToTop ? 'show' : ''}`} onClick={handleBackToTop}>
        <i className="bi bi-arrow-up"></i>
      </div>
    </>
  );
};

export default RoyalHeritageFestia;